import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Search, ShieldCheck, Users, Receipt, UserPlus, Trash2, CheckCircle2, XCircle, Clock, AlertTriangle, Loader2, Zap, CreditCard, Brain, Eye, RotateCcw, RefreshCw, Facebook, Lock, Film } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { useAuth } from '@/contexts/AuthContext';
import api, { formatPaise, formatError } from '@/lib/api';
import { toast } from 'sonner';
import { goalLabel, ctaLabel } from '@/lib/campaign';
import AdminCoupons from '@/components/AdminCoupons';
import AdminLearning from '@/components/AdminLearning';
import Pagination from '@/components/Pagination';

const PAGE_SIZE = 25;

export default function AdminPage() {
  const { user } = useAuth();
  const [tab, setTab] = useState('queue');
  const [queue, setQueue] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [ledger, setLedger] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [refunds, setRefunds] = useState([]);
  const [pages, setPages] = useState({ customers: 1, ledger: 1, refunds: 1 });
  const [totals, setTotals] = useState({ customers: 0, ledger: 0, refunds: 0 });
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [accounts, setAccounts] = useState([]);
  const [newAccount, setNewAccount] = useState('');
  const [stats, setStats] = useState({});
  const [integrations, setIntegrations] = useState({});
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState('');
  const [newAdmin, setNewAdmin] = useState({ email: '', password: '', name: '' });
  const [rejectReason, setRejectReason] = useState('');
  const [showNewAdmin, setShowNewAdmin] = useState(false);

  useEffect(() => {
    loadData();
  }, [tab, pages, search]); // eslint-disable-line react-hooks/exhaustive-deps

  // Search customers shortly after typing stops, from the first page.
  useEffect(() => {
    const t = setTimeout(() => {
      if (searchInput.trim() === search) return;
      setSearch(searchInput.trim());
      setPages(p => ({ ...p, customers: 1 }));
    }, 350);
    return () => clearTimeout(t);
  }, [searchInput, search]);

  const goToPage = (list, page) => setPages(p => ({ ...p, [list]: page }));
  const paged = (list, data) => { setTotals(t => ({ ...t, [list]: data.total })); return data.items; };

  const loadData = async () => {
    setLoading(true);
    try {
      const [statsRes, intRes] = await Promise.all([
        api.get('/api/admin/stats'),
        api.get('/api/admin/integrations'),
      ]);
      setStats(statsRes.data);
      setIntegrations(intRes.data);

      if (tab === 'queue') {
        const { data } = await api.get('/api/admin/review-queue');
        setQueue(data);
      } else if (tab === 'customers') {
        const { data } = await api.get('/api/admin/customers', { params: { q: search, page: pages.customers, page_size: PAGE_SIZE } });
        setCustomers(paged('customers', data));
      } else if (tab === 'ledger') {
        const { data } = await api.get('/api/admin/ledger', { params: { page: pages.ledger, page_size: PAGE_SIZE } });
        setLedger(paged('ledger', data));
      } else if (tab === 'admins') {
        const { data } = await api.get('/api/admin/admins');
        setAdmins(data);
      } else if (tab === 'refunds') {
        const { data } = await api.get('/api/admin/refund-requests', { params: { page: pages.refunds, page_size: PAGE_SIZE } });
        setRefunds(paged('refunds', data));
      } else if (tab === 'accounts') {
        const { data } = await api.get('/api/admin/ad-accounts');
        setAccounts(data);
      }
    } catch {}
    setLoading(false);
  };

  const reviewCampaign = async (campaignId, action, reason = '') => {
    setActionLoading(campaignId);
    try {
      await api.post(`/api/admin/review/${campaignId}`, { action, reason });
      toast.success(`Campaign ${action}d`);
      loadData();
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    }
    setActionLoading('');
  };

  const runAction = async (key, fn, success) => {
    setActionLoading(key);
    try {
      await fn();
      if (success) toast.success(success);
      loadData();
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    }
    setActionLoading('');
  };

  const processRefund = (r) => {
    if (!window.confirm(`Refund ${formatPaise(r.balance_paise)} to ${r.user_email} through Razorpay?`)) return;
    runAction(`refund-${r.id}`, () => api.post(`/api/admin/refund-requests/${r.id}/process`), 'Refund sent');
  };
  const rejectRefund = (r) => {
    const reason = window.prompt('Reason for rejecting this refund request?');
    if (reason === null) return;
    runAction(`refund-${r.id}`, () => api.post(`/api/admin/refund-requests/${r.id}/reject`, { action: 'reject', reason }), 'Request rejected');
  };
  const addAccount = () => runAction('add-account', async () => {
    await api.post('/api/admin/ad-accounts', { meta_account_id: newAccount.trim() });
    setNewAccount('');
  }, 'Ad account added');
  const setCap = (a) => {
    const rupees = window.prompt('Spend cap in ₹ (0 removes the cap)', a.spend_cap_paise ? String(a.spend_cap_paise / 100) : '');
    if (rupees === null) return;
    runAction(`cap-${a.id}`, () => api.post(`/api/admin/ad-accounts/${a.id}/spend-cap`, { spend_cap_paise: Math.round(parseFloat(rupees || '0') * 100) }), 'Spend cap updated');
  };

  const createAdmin = async () => {
    try {
      await api.post('/api/admin/admins', newAdmin);
      toast.success('Admin created');
      setNewAdmin({ email: '', password: '', name: '' });
      setShowNewAdmin(false);
      loadData();
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    }
  };

  const deleteAdmin = async (adminId) => {
    if (!window.confirm('Delete this admin?')) return;
    try {
      await api.delete(`/api/admin/admins/${adminId}`);
      toast.success('Admin deleted');
      loadData();
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    }
  };

  return (
    <div data-testid="admin-page">
      <div className="mb-6">
        <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight font-heading mb-1">Admin</h1>
        <p className="text-sm text-muted-foreground">Manage campaigns, customers, and platform settings.</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        {[
          { label: 'Users', value: stats.total_users || 0, color: 'text-brand-strong' },
          { label: 'Campaigns', value: stats.total_campaigns || 0, color: 'text-rose-700 dark:text-rose-400' },
          { label: 'Pending', value: stats.pending_reviews || 0, color: 'text-amber-700 dark:text-amber-400' },
          { label: 'Revenue', value: formatPaise(stats.total_revenue_paise || 0), color: 'text-money' },
        ].map(s => (
          <div key={s.label} className="p-3 rounded-xl bg-card border-2 border-ink">
            <p className="text-xs text-muted-foreground mb-1">{s.label}</p>
            <p className={`text-lg font-bold font-mono ${s.color}`}>{s.value}</p>
          </div>
        ))}
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="mb-4 bg-card border-2 border-ink h-auto flex-wrap justify-start">
          <TabsTrigger value="queue" className="text-xs" data-testid="admin-tab-queue">Review Queue</TabsTrigger>
          <TabsTrigger value="customers" className="text-xs" data-testid="admin-tab-customers">Customers</TabsTrigger>
          <TabsTrigger value="ledger" className="text-xs" data-testid="admin-tab-ledger">Ledger</TabsTrigger>
          <TabsTrigger value="refunds" className="text-xs" data-testid="admin-tab-refunds">Refunds</TabsTrigger>
          <TabsTrigger value="accounts" className="text-xs" data-testid="admin-tab-accounts">Ad Accounts</TabsTrigger>
          <TabsTrigger value="coupons" className="text-xs" data-testid="admin-tab-coupons">Coupons</TabsTrigger>
          <TabsTrigger value="learning" className="text-xs" data-testid="admin-tab-learning">Learning</TabsTrigger>
          <TabsTrigger value="admins" className="text-xs" data-testid="admin-tab-admins">Admins</TabsTrigger>
          <TabsTrigger value="integrations" className="text-xs" data-testid="admin-tab-integrations">Integrations</TabsTrigger>
        </TabsList>

        <TabsContent value="queue">
          {loading ? <LoadingState /> : queue.length === 0 ? (
            <EmptyState icon={CheckCircle2} text="No campaigns pending review" />
          ) : (
            <div className="space-y-3">
              {queue.map(c => (
                <div key={c.id || c._id} className="p-4 rounded-xl bg-card border-2 border-ink" data-testid={`review-item-${c.id || c._id}`}>
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <div>
                      <p className="text-sm font-semibold">{c.brief?.promotion || 'Campaign'}</p>
                      <p className="text-xs text-muted-foreground">{c.business_name ? `${c.business_name} · ` : ''}{c.user_email} &middot; {goalLabel(c.brief?.goal)} &middot; {formatPaise(c.budget_paise || 0)} over {c.duration_days} days</p>
                    </div>
                    <Badge className="bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-500/30 text-[10px]">Pending</Badge>
                  </div>
                  {c.selected_variant && (
                    <div className="p-3 rounded-lg bg-butter/50 border-2 border-ink/15 mb-3 text-xs flex gap-3">
                      {c.selected_variant.image_url && <img src={`${process.env.REACT_APP_BACKEND_URL}${c.selected_variant.image_url}`} alt="" className="w-20 h-20 rounded-lg object-cover shrink-0" />}
                      <div>
                        <p className="font-medium">{c.selected_variant.headline}</p>
                        <p className="text-muted-foreground mt-1">{c.selected_variant.primary_text}</p>
                        <p className="text-muted-foreground mt-1">{c.selected_variant.description} · Button: {ctaLabel(c.selected_variant.cta)}</p>
                        <p className="text-muted-foreground mt-1">Audience: {c.brief?.location} +{c.brief?.radius_km}km · {c.brief?.age_min}-{c.brief?.age_max} · {c.brief?.gender} · {(c.selected_variant.suggested_audience?.interests || []).join(', ')}</p>
                        {c.brief?.link && <p className="text-muted-foreground mt-1">Link: {c.brief.link}</p>}
                        <p className="mt-1">Policy check: {c.policy_check?.status === 'pass' ? '✅ passed' : `⚠️ ${c.policy_check?.status || 'not run'}`}</p>
                        {c.policy_override && (
                          <div className="mt-2 p-2 rounded-md bg-red-500/10 border border-red-500/30" data-testid="policy-override">
                            <p className="font-semibold text-red-700 dark:text-red-400">Launched despite “Must fix” problems. Check before approving:</p>
                            <ul className="list-disc pl-4 mt-1 space-y-0.5">
                              {(c.policy_override.issues || []).map((i, k) => <li key={k}>{i.message}{i.suggestion ? ` (${i.suggestion})` : ''}</li>)}
                            </ul>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                  <div className="flex gap-2">
                    <Button size="sm" className="bg-money-soft text-money hover:bg-money-soft border border-money/40"
                      onClick={() => reviewCampaign(c.id || c._id, 'approve')}
                      disabled={actionLoading === (c.id || c._id)} data-testid={`admin-approve-ad-btn-${c.id || c._id}`}>
                      {actionLoading === (c.id || c._id) ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3 h-3 mr-1" />} Approve
                    </Button>
                    <Dialog>
                      <DialogTrigger asChild>
                        <Button size="sm" variant="outline" className="text-red-700 dark:text-red-400 border-red-500/30 hover:bg-red-500/10">
                          <XCircle className="w-3 h-3 mr-1" /> Reject
                        </Button>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader><DialogTitle>Reject Campaign</DialogTitle></DialogHeader>
                        <div className="space-y-3">
                          <Label>Reason</Label>
                          <Textarea value={rejectReason} onChange={e => setRejectReason(e.target.value)} placeholder="Reason for rejection..." />
                          <Button className="w-full" variant="destructive"
                            disabled={!rejectReason.trim()}
                            onClick={() => { reviewCampaign(c.id || c._id, 'reject', rejectReason); setRejectReason(''); }}>
                            Confirm Rejection
                          </Button>
                        </div>
                      </DialogContent>
                    </Dialog>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="customers">
          <div className="relative mb-4 max-w-sm">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
            <Input className="pl-9" placeholder="Search name, email or business" aria-label="Search customers"
              value={searchInput} onChange={e => setSearchInput(e.target.value)} data-testid="customer-search" />
          </div>
          {loading ? <LoadingState /> : customers.length === 0 ? (
            <EmptyState icon={Users} text={search ? `No customers match “${search}”` : 'No customers yet'} />
          ) : (
            <div className="space-y-2">
              {customers.map(c => (
                <div key={c.id} className="p-3.5 rounded-xl bg-card border-2 border-ink flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium">{c.name || 'No name'}</p>
                    <p className="text-xs text-muted-foreground">{c.email}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-muted-foreground">{c.campaign_count} campaigns</p>
                    <p className="text-xs font-mono text-money">{formatPaise(c.wallet_balance_paise || 0)}</p>
                  </div>
                </div>
              ))}
              <Pagination page={pages.customers} pageSize={PAGE_SIZE} total={totals.customers} label="customers" onPage={p => goToPage('customers', p)} />
            </div>
          )}
        </TabsContent>

        <TabsContent value="ledger">
          {loading ? <LoadingState /> : ledger.length === 0 ? (
            <EmptyState icon={Receipt} text="No transactions yet" />
          ) : (
            <div className="space-y-2">
              {ledger.map(tx => (
                <div key={tx.id} className="p-3 rounded-xl bg-card border-2 border-ink flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium">{tx.description || tx.type}</p>
                    <p className="text-xs text-muted-foreground">{tx.user_email} &middot; {new Date(tx.created_at).toLocaleDateString('en-IN')}</p>
                  </div>
                  <span className={`text-sm font-bold font-mono ${['debit', 'withdrawal', 'plan'].includes(tx.type) ? 'text-rose-700 dark:text-rose-400' : 'text-money'}`}>
                    {['debit', 'withdrawal', 'plan'].includes(tx.type) ? '-' : '+'}₹{(tx.amount_paise / 100).toFixed(2)} <span className="text-[10px] text-muted-foreground font-normal">{tx.type} · {tx.status}</span>
                  </span>
                </div>
              ))}
              <Pagination page={pages.ledger} pageSize={PAGE_SIZE} total={totals.ledger} label="entries" onPage={p => goToPage('ledger', p)} />
            </div>
          )}
        </TabsContent>

        <TabsContent value="refunds">
          {loading ? <LoadingState /> : refunds.length === 0 ? (
            <EmptyState icon={RotateCcw} text="No refund requests" />
          ) : (
            <div className="space-y-2">
              {refunds.map(r => (
                <div key={r.id} className="p-3.5 rounded-xl bg-card border-2 border-ink flex items-center justify-between gap-3" data-testid={`refund-request-${r.id}`}>
                  <div>
                    <p className="text-sm font-medium">{r.user_email}</p>
                    <p className="text-xs text-muted-foreground">{new Date(r.created_at).toLocaleDateString('en-IN')} · {r.status}{r.amount_paise ? ` · refunded ${formatPaise(r.amount_paise)}` : ''}{r.note ? ` · ${r.note}` : ''}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-mono text-money">{formatPaise(r.balance_paise)}</span>
                    {r.status === 'pending' && (
                      <>
                        <Button size="sm" className="gradient-bg" disabled={actionLoading === `refund-${r.id}`} onClick={() => processRefund(r)}>Refund now</Button>
                        <Button size="sm" variant="outline" onClick={() => rejectRefund(r)}>Reject</Button>
                      </>
                    )}
                  </div>
                </div>
              ))}
              <Pagination page={pages.refunds} pageSize={PAGE_SIZE} total={totals.refunds} label="requests" onPage={p => goToPage('refunds', p)} />
            </div>
          )}
        </TabsContent>

        <TabsContent value="accounts">
          <div className="flex gap-2 mb-4">
            <Input placeholder="act_1234567890" value={newAccount} onChange={e => setNewAccount(e.target.value)} data-testid="new-ad-account-input" />
            <Button className="gradient-bg" onClick={addAccount} disabled={!newAccount.trim() || actionLoading === 'add-account'}>Add</Button>
          </div>
          {loading ? <LoadingState /> : accounts.length === 0 ? (
            <EmptyState icon={Facebook} text="No platform ad accounts yet. While sandbox mode is on, campaigns use the sandbox account." />
          ) : (
            <div className="space-y-2">
              {accounts.map(a => (
                <div key={a.id} className="p-3.5 rounded-xl bg-card border-2 border-ink flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <p className="text-sm font-medium">{a.name || a.meta_account_id} <span className="text-xs text-muted-foreground">{a.meta_account_id}</span></p>
                    <p className="text-xs text-muted-foreground">
                      {a.customers} customers · spent {formatPaise(a.amount_spent_paise || 0)} · cap {a.spend_cap_paise ? formatPaise(a.spend_cap_paise) : 'none'} · Meta status {a.meta_account_status === 1 ? 'active' : (a.meta_account_status ?? '–')}
                      {a.last_error && <span className="text-red-700 dark:text-red-400"> · {a.last_error}</span>}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className={a.status === 'active' ? 'bg-money-soft text-money border-money/40' : 'bg-red-500/10 text-red-700 dark:text-red-400 border-red-500/30'}>{a.status}</Badge>
                    <Button size="sm" variant="ghost" onClick={() => runAction(`r-${a.id}`, () => api.post(`/api/admin/ad-accounts/${a.id}/refresh`))}><RefreshCw className="w-3.5 h-3.5" /></Button>
                    <Button size="sm" variant="outline" onClick={() => setCap(a)}>Cap</Button>
                    {a.status === 'active'
                      ? <Button size="sm" variant="outline" className="text-red-700 dark:text-red-400 border-red-500/30" onClick={() => window.confirm('Quarantine this account and pause its live campaigns?') && runAction(`q-${a.id}`, () => api.post(`/api/admin/ad-accounts/${a.id}/quarantine`), 'Account quarantined')}>Quarantine</Button>
                      : <Button size="sm" variant="outline" onClick={() => runAction(`a-${a.id}`, () => api.post(`/api/admin/ad-accounts/${a.id}/activate`), 'Account activated')}>Activate</Button>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="coupons">
          <AdminCoupons />
        </TabsContent>

        <TabsContent value="learning">
          <AdminLearning />
        </TabsContent>

        <TabsContent value="admins">
          {loading ? <LoadingState /> : (
            <div>
              {user?.role === 'super_admin' && (
                <div className="mb-4">
                  {!showNewAdmin ? (
                    <Button size="sm" className="gradient-bg" onClick={() => setShowNewAdmin(true)} data-testid="add-admin-btn">
                      <UserPlus className="w-4 h-4 mr-1" /> Add Admin
                    </Button>
                  ) : (
                    <div className="p-4 rounded-xl bg-card border-2 border-ink space-y-3">
                      <div className="grid grid-cols-3 gap-3">
                        <Input placeholder="Name" value={newAdmin.name} onChange={e => setNewAdmin(a => ({ ...a, name: e.target.value }))} data-testid="new-admin-name" />
                        <Input placeholder="Email" value={newAdmin.email} onChange={e => setNewAdmin(a => ({ ...a, email: e.target.value }))} data-testid="new-admin-email" />
                        <Input placeholder="Password" type="password" value={newAdmin.password} onChange={e => setNewAdmin(a => ({ ...a, password: e.target.value }))} data-testid="new-admin-password" />
                      </div>
                      <div className="flex gap-2">
                        <Button size="sm" className="gradient-bg" onClick={createAdmin} data-testid="save-admin-btn">Create</Button>
                        <Button size="sm" variant="outline" onClick={() => setShowNewAdmin(false)}>Cancel</Button>
                      </div>
                    </div>
                  )}
                </div>
              )}
              <div className="space-y-2">
                {admins.map(a => (
                  <div key={a.id} className="p-3.5 rounded-xl bg-card border-2 border-ink flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-brand border-2 border-ink flex items-center justify-center">
                        <ShieldCheck className="w-4 h-4 text-brand-strong" />
                      </div>
                      <div>
                        <p className="text-sm font-medium">{a.name}</p>
                        <p className="text-xs text-muted-foreground">{a.email}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className={a.is_super_admin ? 'bg-brand text-[#141414] border-ink' : 'bg-muted'}>
                        {a.is_super_admin ? 'Super Admin' : 'Admin'}
                      </Badge>
                      {user?.role === 'super_admin' && !a.is_super_admin && (
                        <Button size="sm" variant="ghost" className="h-7 text-red-700 dark:text-red-400 hover:bg-red-500/10"
                          onClick={() => deleteAdmin(a.id)} data-testid={`delete-admin-${a.id}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </TabsContent>

        <TabsContent value="integrations">
          <div className="space-y-3">
            {Object.entries(integrations).filter(([key]) => key !== 'recent_errors').map(([key, val]) => (
              <div key={key} className="p-4 rounded-xl bg-card border-2 border-ink flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${val.status === 'active' ? 'bg-money-soft' : 'bg-amber-500/10'}`}>
                    {key === 'meta' ? <Eye className="w-4 h-4 text-blue-700 dark:text-blue-400" /> : key === 'razorpay' ? <CreditCard className="w-4 h-4 text-blue-700 dark:text-blue-400" /> : key === 'encryption' ? <Lock className="w-4 h-4 text-blue-700 dark:text-blue-400" /> : key === 'video' ? <Film className="w-4 h-4 text-blue-700 dark:text-blue-400" /> : <Brain className="w-4 h-4 text-brand-strong" />}
                  </div>
                  <div>
                    <p className="text-sm font-semibold capitalize">{key}</p>
                    <p className="text-xs text-muted-foreground">{val.message}</p>
                  </div>
                </div>
                <Badge variant="outline" className={val.status === 'active' ? 'bg-money-soft text-money border-money/40' : val.status === 'error' ? 'bg-red-500/10 text-red-700 dark:text-red-400 border-red-500/30' : 'bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-500/30'}>
                  {val.status === 'active' ? <Zap className="w-3 h-3 mr-1" /> : val.status === 'error' ? <AlertTriangle className="w-3 h-3 mr-1" /> : <Clock className="w-3 h-3 mr-1" />}
                  {val.status}
                </Badge>
              </div>
            ))}
            <div className="p-4 rounded-xl bg-card border-2 border-ink">
              <p className="text-sm font-semibold mb-2">Recent API errors</p>
              {(integrations.recent_errors || []).length === 0 ? <p className="text-xs text-muted-foreground">No errors.</p> : (
                <div className="space-y-1.5">
                  {integrations.recent_errors.map((e, i) => (
                    <p key={i} className="text-xs"><span className="text-muted-foreground">{e.created_at ? new Date(e.created_at).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }) : ''}</span> <span className="font-semibold">{e.service} · {e.operation}</span> <span className="text-muted-foreground">{e.message}</span></p>
                  ))}
                </div>
              )}
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function LoadingState() {
  return <div className="flex justify-center py-12"><div className="flex gap-1"><span className="loading-dot" /><span className="loading-dot" /><span className="loading-dot" /></div></div>;
}

function EmptyState({ icon: Icon, text }) {
  return (
    <div className="text-center py-12 rounded-2xl bg-card border-2 border-dashed border-ink/40">
      <Icon className="w-8 h-8 text-muted-foreground/30 mx-auto mb-2" />
      <p className="text-sm text-muted-foreground">{text}</p>
    </div>
  );
}
